<template>
    <button v-if="hasTitle" type="button" class="widget-row" :class="{ 'is-selected': isSelected }" :role="showRadio ? 'radio' : null" :aria-checked="showRadio ? (selected ? 'true' : 'false') : null" @click="onActivate">
        <span v-if="showRadio" class="widget-row__radio" :class="{ 'is-checked': selected }" aria-hidden="true"></span>

        <span class="widget-row__body">
            <span class="widget-row__title">{{ displayTitle }}</span>
            <span v-if="hasMeta" class="widget-row__meta">
                <span v-if="hasReference" class="widget-row__reference">{{ displayReference }}</span>
                <span v-if="showDescriptionDot" class="widget-row__dot" :style="descriptionDotStyle" aria-hidden="true"></span>
                <span v-if="hasDescription" class="widget-row__description">{{ displayDescription }}</span>
            </span>
        </span>

        <span v-if="hasAside" class="widget-row__aside">
            <span v-if="showValueDot" class="widget-row__dot" :style="valueDotStyle" aria-hidden="true"></span>
            <span v-if="hasValue" class="widget-row__value">{{ displayValue }}</span>
            <span v-if="hasLabel" class="widget-row__label">{{ displayLabel }}</span>
            <i v-if="showsChevron" class="widget-row__chevron i-andy-chevron-right i-andy-s" aria-hidden="true"></i>
        </span>
    </button>
</template>

<script>
export default {
    name: 'WidgetRow',
    props: {
        title: { type: String, default: '' },
        reference: { type: String, default: '' },
        description: { type: String, default: '' },
        showDescriptionDot: { type: Boolean, default: false },
        descriptionDotColor: { type: String, default: '' },
        value: { type: String, default: '' },
        showValueDot: { type: Boolean, default: false },
        valueDotColor: { type: String, default: '' },
        label: { type: String, default: '' },
        showRadio: { type: Boolean, default: false },
        selected: { type: Boolean, default: false },
        showChevron: { type: Boolean, default: true }
    },
    computed: {
        hasTitle() {
            return this.hasText(this.title)
        },
        displayTitle() {
            return this.title.trim()
        },
        hasReference() {
            return this.hasText(this.reference)
        },
        displayReference() {
            return this.reference.trim()
        },
        hasDescription() {
            return this.hasText(this.description)
        },
        displayDescription() {
            return this.description.trim()
        },
        hasValue() {
            return this.hasText(this.value)
        },
        displayValue() {
            return this.value.trim()
        },
        hasLabel() {
            return this.hasText(this.label)
        },
        displayLabel() {
            return this.label.trim()
        },
        hasMeta() {
            return this.hasReference || this.showDescriptionDot || this.hasDescription
        },
        showsChevron() {
            return this.showChevron && !this.showRadio
        },
        hasAside() {
            return this.showValueDot || this.hasValue || this.hasLabel || this.showsChevron
        },
        isSelected() {
            return this.showRadio && this.selected
        },
        descriptionDotStyle() {
            return this.dotStyle(this.descriptionDotColor)
        },
        valueDotStyle() {
            return this.dotStyle(this.valueDotColor)
        }
    },
    methods: {
        hasText(value) {
            return typeof value === 'string' && value.trim() !== ''
        },
        dotStyle(color) {
            if (!this.hasText(color)) {
                return null
            }
            return { backgroundColor: color.trim() }
        },
        onActivate() {
            if (this.showRadio) {
                this.$emit('select')
                return
            }
            this.$emit('navigate')
        }
    }
}
</script>

<style lang="scss" scoped>
.widget-row {
    @include display-flex();
    @include align-items(center);
    box-sizing: border-box;
    width: 100%;
    min-height: rem(44px);
    margin: 0;
    padding: rem(12px) rem(16px);
    border: 0;
    background-color: $color-white;
    gap: rem(12px);
    text-align: left;
    font-family: $text;
    color: $neutro-s80;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus,
    &:focus-visible,
    &:active {
        outline: none;
    }

    &.is-selected,
    &.is-selected:active {
        background-color: $main-t90-app;
    }

    @media (hover: hover) {
        &:hover:not(.is-selected),
        &:hover:not(.is-selected):active {
            background-color: $neutro-t80;
        }
    }
}

.widget-row__radio {
    @include display-flex();
    @include align-items(center);
    @include justify-content(center);
    @include border-radius(50%);
    flex-shrink: 0;
    width: rem(20px);
    height: rem(20px);
    border: rem(2px) solid $neutro-s60;
    background-color: transparent;

    &.is-checked {
        border-color: $main;

        &::after {
            @include border-radius(50%);
            content: '';
            width: rem(10px);
            height: rem(10px);
            background-color: $main;
        }
    }
}

.widget-row__body {
    @include display-flex();
    @include flex-direction(column);
    @include align-items(flex-start);
    flex: 1;
    min-width: 0;
    gap: rem(4px);
}

.widget-row__title {
    @include font-size(s);
    @include text-ellipsis();
    display: block;
    width: 100%;
    font-family: $text-bold;
    line-height: rem(20px);
    color: $neutro-s80;
}

.widget-row__meta {
    @include display-flex();
    @include align-items(center);
    width: 100%;
    min-width: 0;
    gap: rem(8px);
}

.widget-row__reference {
    @include font-size(xs);
    @include border-radius(rem(4px));
    flex-shrink: 0;
    padding: rem(2px) rem(6px);
    background-color: $neutro;
    font-family: $text-bold;
    line-height: rem(16px);
    color: $neutro-s80;
    white-space: nowrap;
}

.widget-row__description {
    @include font-size(xs);
    @include text-ellipsis();
    flex: 1;
    min-width: 0;
    font-family: $text;
    line-height: rem(16px);
    color: $neutro-s60;
}

.widget-row__dot {
    @include border-radius(50%);
    flex-shrink: 0;
    width: rem(8px);
    height: rem(8px);
}

.widget-row__aside {
    @include display-flex();
    @include align-items(center);
    flex-shrink: 0;
    gap: rem(8px);
}

.widget-row__value {
    @include font-size(s);
    flex-shrink: 0;
    font-family: $text-bold;
    line-height: rem(20px);
    color: $neutro-s80;
    white-space: nowrap;
}

.widget-row__label {
    @include font-size(xs);
    @include text-ellipsis();
    @include border-radius(rem(4px));
    flex-shrink: 1;
    min-width: 0;
    max-width: rem(102px);
    padding: rem(2px) rem(8px);
    background-color: $neutro;
    font-family: $text-bold;
    line-height: rem(16px);
    color: $main;
}

.widget-row__chevron {
    flex-shrink: 0;
    color: $neutro-s60;
}
</style>
